import React from 'react';
import { CheckCircle, Calendar, Users, Bell, Activity, Shield, Smartphone, Heart, HeartHandshake } from 'lucide-react';

// Brand colors
const colors = {
  tealGreen: '#3AB795',
  lightMint: '#DFF5F0',
  softGreen: '#88C869',
  emeraldGreen: '#2B9C6A',
  deepTeal: '#174840',
  cream: '#FDF9F3',
  brightGreen: '#A4DC4C',
  white: '#FFFFFF',
  black: '#000000'
};

const containerStyle = {
  maxWidth: '1200px',
  margin: '0 auto',
  padding: '0 1.5rem'
};

const pill = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.35rem 0.75rem',
  borderRadius: '9999px',
  backgroundColor: '#ffffffaa',
  border: `1px solid ${colors.lightMint}`,
  color: colors.deepTeal,
  fontSize: '0.9rem',
  fontWeight: 600
} as const;

const cardStyle = {
  backgroundColor: colors.white,
  borderRadius: '1rem',
  padding: '2rem',
  boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  transition: 'box-shadow 0.3s ease',
  textAlign: 'left' as const
};

type Feature = {
  icon: React.ReactNode;
  title: string;
  description: string;
};

const FeatureItem = ({ feature }: { feature: Feature }) => (
  <div style={{ display: 'flex', gap: '0.75rem' }}>
    <div
      aria-hidden="true"
      style={{
        width: '48px',
        height: '48px',
        borderRadius: '0.75rem',
        backgroundColor: colors.lightMint,
        color: colors.tealGreen,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      }}
    >
      {feature.icon}
    </div>
    <div>
      <h4 style={{ margin: 0, color: colors.deepTeal, fontSize: '1.05rem', fontWeight: 700 }}>
        {feature.title}
      </h4>
      <p style={{ margin: '0.25rem 0 0', color: colors.emeraldGreen, lineHeight: 1.6 }}>
        {feature.description}
      </p>
    </div>
  </div>
);

const CharlieCheckLanding = () => {
 

  // Persona-grouped features
  const personaBlocks: {
    heading: string;
    question: string;
    subtitle: string;
    icon: React.ReactNode;
    features: Feature[];
  }[] = [
    {
      heading: 'For Managing Your Own Health',
      question: 'Forget to take that medication again?',
      subtitle:
        'Stay on top of medications, therapy, and wellness goals with intelligent reminders and a plan that adapts to you.',
      icon: <Heart size={24} />,
      features: [
        {
          icon: <Calendar size={24} />,
          title: 'Intelligent Scheduling',
          description:
            'Set reminders by voice, photo, or text. Charlie learns your routines, tracks progress, and keeps you on track.'
        },
        {
          icon: <Activity size={24} />,
          title: 'Progress Tracking',
          description:
            'Monitor activity patterns, medication adherence, and wellness goals. Celebrate milestones and spot trends early.'
        }
      ]
    },     
    {
      heading: 'For Caregivers, Providers, & Payers',
      question: 'Overwhelmed with managing follow-ups?',
      subtitle:
        'Coordinate support with less overwhelm. Track data and clearly see adherence trends.',
      icon: <Users size={24} />,
      features: [
        {
          icon: <Users size={24} />,
          title: 'Care Circles',
          description:
            'Add family members, caregivers, and provider connections for support. Share updates and encouragement in one place.'
        },
        {
          icon: <Bell size={24} />,
          title: 'Smart Notifications',
          description:
            'Multi-channel nudges via app, text, or phone. Charlie knows when to send a gentle reminder versus an urgent alert to your support team.'
        },
        {
          icon: <HeartHandshake size={24} />,
          title: 'Personzlized In Real-Time',
          description:
            'Providers, caregivers, and payers collaborate to tailor Charlie’s AI—delivering instant education, keeping your care circle engaged, and driving better adherence and outcomes.'
        }
      ]
    },
    {
      heading: 'For Communities',
      question: 'Want to get education and track off-site support of vulnerable citizens?',
      subtitle:
        'Extend educational support and intelligent reminders between visits. Receive escalations when intervention may be needed.',
      icon: <Shield size={24} />,
      features: [
        {
          icon: <Smartphone size={24} />,
          title: 'Case Worker Support',
          description:
            'Engage citizens in a new way with active service planning and making it faster and easier to connect with all of the necessary resources like housing, food assistance, healthcare, and counseling.'
        },
        {
          icon: <Shield size={24} />,
          title: 'Secure & Private',
          description:
            'Enterprise-grade HIPAA-compliant security and processes put each user in full control of their data and privacy without exposure.'
        }
      ]
    }
   
  ];

  return (
    <div
      style={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${colors.lightMint} 0%, ${colors.cream} 100%)`,
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      }}
    >
      {/* Header */}
      <header style={containerStyle}>
        <div style={{ textAlign: 'center', paddingTop: '2rem', paddingBottom: '0.1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.5rem' }}>
            <div style={{ position: 'relative' }}>
              <div
                style={{
                  borderRadius: '1rem',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative'
                }}
              >
                <img src="/img/cc_logo_image.png" alt="CharlieCheck Logo" style={{ height: '180px' }} />
              </div>
            </div>
          </div>
          <h1
            style={{
              fontSize: '3rem',
              fontWeight: 'bold',
              color: colors.deepTeal,
              margin: '1rem 0'
            }}
          >
            Hi, I&apos;m Charlie.
          </h1>
        </div>
      </header>

      {/* Hero Section */}
      <section style={{ ...containerStyle, textAlign: 'center', padding: '0.5rem 1.5rem' }}>
        <div style={{ marginBottom: '0.5rem' }}>
          <span style={pill} aria-label="Social AI Care Companion">
            <Smartphone size={18} />
            Social AI Care Companion
          </span>
        </div>

        <h2
          style={{
            fontSize: '2.25rem',
            fontWeight: 'bold',
            color: colors.deepTeal,
            marginBottom: '1rem'
          }}
        >
          Charlie is your social AI care companion for individuals, families, providers, payers, and communities — supporting each other to improve care management outcomes.
        </h2>
        <p
          style={{
            fontSize: '1.125rem',
            color: colors.emeraldGreen,
            maxWidth: '48rem',
            margin: '0 auto 2.25rem auto',
            lineHeight: '1.6'
          }}
        >
          Organize dosing and any health or wellness check-in schedules with automated reminders, tracking, and engagement with your own personal care circle. Provide instant access to education and visibility across your care circle.
        </p>

        <h2
          style={{
            fontSize: '2.25rem',
            fontWeight: 'bold',
            color: colors.deepTeal,
            marginBottom: '1rem'
          }}
        >

          Now Available
          
        </h2>
        
          <div  style={{
            marginBottom: '2rem'
          }}>

            <a href="https://apps.apple.com/us/app/charliecheck/id6754394287">
              <img src="/img/apple_logo_appstore.png" alt="Apple Appstore Link" style={{ width: '220px' }} />
            </a>

          </div>

          
          <div>

            <a href="https://play.google.com/store/apps/details?id=com.charliecheck.app">
              <img src="/img/google_logo_playstore.png" alt="Google Play Link" style={{ width: '220px' }} />
            </a>

          </div>

          
        
      </section>

      {/* Personas + Capabilities */}
      <section style={{ ...containerStyle, padding: '0.5rem 1.5rem' }}>
        <h2
          style={{
            fontSize: '2rem',
            fontWeight: 'bold',
            textAlign: 'center',
            color: colors.deepTeal,
            marginBottom: '1.25rem'
          }}
        >
          Who Charlie Helps
        </h2>
        <p
          style={{
            textAlign: 'center',
            color: colors.emeraldGreen,
            maxWidth: '48rem',
            margin: '0 auto 2.25rem',
            lineHeight: 1.6
          }}
        >
          Start with the role that best fits you—then see exactly how Charlie supports your day-to-day.
        </p>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '2rem'
          }}
        >
          {personaBlocks.map((block, idx) => (
            <div key={idx} style={cardStyle}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <div style={{ ...pill, backgroundColor: colors.lightMint, borderColor: colors.lightMint }}>
                  {block.icon}
                  {block.heading}
                </div>
              </div>
               <p style={{ color: colors.emeraldGreen, marginTop: '0.5rem', marginBottom: '1.25rem', lineHeight: 1.6 }}>
                <strong>{block.question}</strong>
              </p>
              <p style={{ color: colors.emeraldGreen, marginTop: '0.5rem', marginBottom: '1.25rem', lineHeight: 1.6 }}>
                {block.subtitle}
              </p>

              <div style={{ display: 'grid', gap: '1rem' }}>
                {block.features.map((f, i) => (
                  <FeatureItem key={i} feature={f} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section style={{ ...containerStyle, padding: '3rem 1.5rem' }}>
        <div
          style={{
            backgroundColor: colors.white,
            borderRadius: '1.5rem',
            padding: '3rem',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
          }}
        >
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <h2
              style={{
                fontSize: '2rem',
                fontWeight: 'bold',
                color: colors.deepTeal,
                marginBottom: '1.5rem'
              }}
            >
              The Challenge We&apos;re Solving
            </h2>
            <p
              style={{
                fontSize: '1.125rem',
                color: colors.emeraldGreen,
                maxWidth: '48rem',
                margin: '0 auto',
                lineHeight: '1.6'
              }}
            >
              Over one hundred million Americans live with chronic conditions and as many as nine million are receiving treatment for substance use disorder. We see them. We also see the tens of millions of caregivers including family, providers, and community case workers who support them. 
              Our busy schedules and responsibilities and tightening of resources can make care management, support and adherence a real challenge. We aspire to change that.
            </p>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '3rem',
              alignItems: 'start'
            }}
          >
            <div>
              <h3
                style={{
                  fontSize: '1.5rem',
                  fontWeight: '600',
                  color: colors.deepTeal,
                  marginBottom: '1.5rem'
                }}
              >
                Why Current Solutions Fall Short
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {[
                  'Simple reminders do not capture the complexity of real life',
                  'Insufficient coordination between caregivers',
                  'Lack of intelligent escalation when things go wrong',
                  'Missing insights into patterns and progress',
                  'Lack of personalized educational support',
                  'Inability to quickly adapt to changes in routine, condition, or environment'
                ].map((item, index) => (
                  <div key={index} style={{ display: 'flex', alignItems: 'flex-start' }}>
                    <div
                      style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: colors.tealGreen,
                        marginTop: '8px',
                        marginRight: '12px',
                        flexShrink: 0
                      }}
                    />
                    <span style={{ color: colors.emeraldGreen, lineHeight: '1.6' }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3
                style={{
                  fontSize: '1.5rem',
                  fontWeight: '600',
                  color: colors.deepTeal,
                  marginBottom: '1.5rem'
                }}
              >
                The CharlieCheck Difference
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {[
                  'AI that understands context and adapts to your needs',
                  'Seamless coordination across your care circle (family, caregivers and providers, case workers)',
                  'Smart escalation when intervention may be needed',
                  'Actionable insights to improve outcomes',
                  'Personalized educational support and resources',
                ].map((item, index) => (
                  <div key={index} style={{ display: 'flex', alignItems: 'flex-start' }}>
                    <CheckCircle
                      size={20}
                      color={colors.tealGreen}
                      style={{ marginTop: '2px', marginRight: '12px', flexShrink: 0 }}
                    />
                    <span style={{ color: colors.emeraldGreen, lineHeight: '1.6' }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{ ...containerStyle, padding: '1rem 0.5rem', textAlign: 'center' }}>
        <div style={{ maxWidth: '32rem', margin: '0 auto' }}>
          <h2
            style={{
              fontSize: '2rem',
              fontWeight: 'bold',
              color: colors.deepTeal,
              marginBottom: '1.5rem'
            }}
          >
            {/* Contact Us */}
          </h2>
          {/* <p
            style={{
              fontSize: '1.125rem',
              color: colors.emeraldGreen,
              marginBottom: '2rem',
              lineHeight: '1.6'
            }}
          >
            Join other families, caregivers, providers, payers and communities who want a better way to support those they care for.
          </p> */}
          {/* {!isSubmitted && (
            <button
              onClick={() => {
                const emailInput = document.querySelector('input[type="email"]') as HTMLInputElement;
                emailInput?.scrollIntoView({ behavior: 'smooth' });
                emailInput?.focus();
              }}
              style={{
                padding: '1rem 2rem',
                borderRadius: '0.5rem',
                backgroundColor: colors.tealGreen,
                color: colors.white,
                fontWeight: '600',
                fontSize: '1.125rem',
                border: 'none',
                cursor: 'pointer',
                transition: 'opacity 0.3s ease'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.9')}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
              aria-label="Request Early Access"
            >
              Request Early Access
            </button>
          )} */}
        </div>


      {/* Contact Us Form
      <div style={{ maxWidth: '28rem', margin: '0 auto 3rem auto' }}>
          {!isSubmitted ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                // Trigger button click logic to preserve existing handling
              }}
              aria-label="Contact Us"
              style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
            >
              <input type="hidden" value={src} readOnly />

              <label style={{ textAlign: 'left', fontSize: '0.95rem', color: colors.deepTeal, fontWeight: 600 }}>
                Email<span aria-hidden="true" style={{ color: colors.tealGreen }}> *</span>
              </label>
              <input
                aria-label="Your Email address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="e.g. name@email.com"
                style={{
                  padding: '0.75rem 1rem',
                  borderRadius: '0.5rem',
                  border: '1px solid #d1d5db',
                  fontSize: '1rem',
                  outline: 'none'
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSubmit(e);
                }}
                required
              />

              <label style={{ textAlign: 'left', fontSize: '0.95rem', color: colors.deepTeal, fontWeight: 600 }}>
                How can we help? <span style={{ fontWeight: 400, color: colors.emeraldGreen }}>(optional)</span>
              </label>
              <textarea
                aria-label="Contact CharlieCheck"
                value={why}
                onChange={(e) => setWhy(e.target.value)}
                placeholder="e.g. I'd like to set up time for my organization to learn more."
                style={{
                  padding: '0.75rem 1rem',
                  borderRadius: '0.5rem',
                  border: '1px solid #d1d5db',
                  height: '130px',
                  fontSize: '1rem',
                  outline: 'none',
                  resize: 'vertical'
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSubmit(e);
                }}
              />

              <button
                type="button"
                onClick={handleSubmit}
                disabled={isLoading || !email || !/\S+@\S+\.\S+/.test(email)}
                style={{
                  padding: '0.75rem 2rem',
                  borderRadius: '0.5rem',
                  backgroundColor: colors.tealGreen,
                  color: colors.white,
                  fontWeight: '600',
                  border: 'none',
                  cursor: isLoading || !email ? 'not-allowed' : 'pointer',
                  opacity: isLoading || !email ? 0.5 : 1,
                  transition: 'opacity 0.3s ease'
                }}
                aria-disabled={isLoading || !email || !/\S+@\S+\.\S+/.test(email)}>
                {isLoading ? 'Contacting Charlie…' : 'Submit'}
              </button>

              <p
                style={{
                  fontSize: '0.875rem',
                  color: colors.emeraldGreen
                }}
              >
                By continuing you agree to receive email notifications from Charlie Check. You may unsubscribe at any time.
              </p>
            </form>
          ) : (
            <div
              role="status"
              style={{
                textAlign: 'center',
                padding: '1.5rem',
                borderRadius: '0.5rem',
                backgroundColor: colors.lightMint
              }}
            >
              <CheckCircle size={48} color={colors.tealGreen} style={{ margin: '0 auto 1rem auto' }} />
              <h3
                style={{
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  color: colors.deepTeal,
                  marginBottom: '0.5rem'
                }}
              >
                Welcome to the team!
              </h3>
              <p style={{ color: colors.emeraldGreen }}>You&apos;re on the early access waitlist. We&apos;ll notify you when Charlie is ready.</p>
            </div>
          )}
        </div> */}

      </section>

      {/* Footer */}
      <footer
        style={{
          borderTop: `1px solid ${colors.lightMint}`,
          backgroundColor: colors.cream,
          padding: '3rem 0'
        }}
      >
        <div style={containerStyle}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '1rem'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div>
                <img src="/img/cc_logo_head.png" alt="CharlieCheck Logo" style={{ height: '44px' }} />
              </div>
              <span
                style={{
                  fontWeight: '600',
                  color: colors.deepTeal
                }}
              ></span>
            </div>
            <div
              style={{
                display: 'flex',
                gap: '1.5rem',
                fontSize: '0.875rem'
              }}
            >
              <a
                href="terms.html"
                style={{
                  color: colors.emeraldGreen,
                  textDecoration: 'none'
                }}
                onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
              >
                Terms of Service
              </a>
              <a
                href="privacy.html"
                style={{
                  color: colors.emeraldGreen,
                  textDecoration: 'none'
                }}
                onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
              >
                Privacy Policy
              </a>
              <a
                href="mailto:shyfttoai@gmail.com"
                style={{
                  color: colors.emeraldGreen,
                  textDecoration: 'none'
                }}
                onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
              >
                Contact
              </a>
            </div>
          </div>
          <div
            style={{
              textAlign: 'center',
              marginTop: '2rem',
              paddingTop: '2rem',
              borderTop: `1px solid ${colors.lightMint}`,
              fontSize: '0.875rem',
              color: colors.emeraldGreen
            }}
          >
            www.CharlieCheck.com   -   © 2026 ShyftAI   -   All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CharlieCheckLanding;
